/**
 * Análises › the market-standard analyses (D531; research doc 05 §3 rows 1, 2, 6, 7, 9-11, 19-23, 25, 26, 29-31, 33-35, 38 and §5): the insights the new
 * panels (Operação, Crescimento, Oferta, Mapa, Qualidade) and the additions to Financeiro are made of. An insight is DATA over the governed catalog -
 * a few query blocks (never SQL) and a pure function that turns the answers into the model a chart draws. Pure: no React, no clock, no I/O; the
 * formatting of a figure comes in through `env.num` (so the same model serves every language and market).
 *
 * Rules that hold here and nowhere else:
 *  - money is never summed or drawn across currencies: a money insight is a list of GROUPS, one per currency;
 *  - a percentile of a bucketed distribution is an approximation (`percentileFromBuckets` says so) and the screen says "about";
 *  - a figure that is missing stays missing (an empty cell), a count that is absent is a real zero;
 *  - a goal (meta) is looked up for the ONE market the answer covers; across several markets there is no single goal to read against;
 *  - the pickup cell comes only from the k-floored view (the catalog guarantees it) and a key that does not rebuild a cell is dropped.
 */
import { type AnalyticsBiQueryView } from '@vello/api-client';
import {
  BUCKET_EDGES,
  type BiTarget,
  type Granularity,
  type Percentile,
  type ResultCell,
  type ResultRow,
  bucketRanges,
  cumulativeShares,
  percentileFromBuckets,
  pickupCellBounds,
  targetStatus,
  type TargetStatus,
} from '@vello/analytics-semantic';
import { type BiBlock } from './bi-panels.js';
import { trendKeys } from './bi-core.js';

export type InsightKind = 'scorecard' | 'heatmap' | 'histogram' | 'funnel' | 'cohort' | 'waterfall' | 'columns' | 'combo' | 'stackedBars' | 'stackedArea' | 'geo' | 'table';

/** One query of an insight. `grain`: asked at the screen's time grain; `comparison`: asked with the screen's comparison (default none). */
export interface InsightBlock {
  readonly id: string;
  readonly block: BiBlock;
  readonly grain?: boolean;
  readonly comparison?: boolean;
  /** The dimension of the bar this block keeps showing in full (the chart is the filter's own control). */
  readonly ignoreBar?: string;
}

/** What a builder needs from the screen: how a cell becomes the number a chart draws, and the goals in force. */
export interface InsightEnv {
  /** The time grain the screen draws trends at (`grain` series use it). */
  readonly grain: Granularity;
  readonly num: (unit: string, cell: ResultCell, currency: string | undefined) => number;
  readonly targets: readonly (BiTarget & { readonly version?: number })[];
}

export type InsightViews = Readonly<Record<string, AnalyticsBiQueryView | undefined>>;

// ───────────────────────────── the models ─────────────────────────────

export interface ScoreItem {
  readonly metric: string;
  readonly unit: string;
  readonly value: number | null;
  readonly baseline: number | null;
  /** The goal of the one market in view, when it has one. */
  readonly target: BiTarget | null;
  readonly status: TargetStatus | null;
}
export interface ScorecardModel {
  readonly kind: 'scorecard';
  readonly items: readonly ScoreItem[];
  /** The one market the goals were read for; `null` across several markets (the scorecard says a goal needs one market). */
  readonly marketId: string | null;
}
export interface HeatmapModel {
  readonly kind: 'heatmap';
  readonly metric: string;
  readonly unit: string;
  readonly rowDimension: string;
  readonly colDimension: string;
  readonly rowKeys: readonly string[];
  readonly colKeys: readonly string[];
  readonly values: readonly (readonly (number | null)[])[];
}
export interface HistogramBin {
  readonly key: string;
  readonly from: number;
  readonly to: number;
  readonly count: number;
}
export interface HistogramModel {
  readonly kind: 'histogram';
  readonly dimension: string;
  readonly metric: string;
  readonly unit: 'duration_s' | 'delta_s';
  readonly bins: readonly HistogramBin[];
  /** Records with no value in the distribution (a ride nobody took has no match time). */
  readonly missing: number;
  readonly total: number;
  readonly p50: Percentile | undefined;
  readonly p90: Percentile | undefined;
  /** Cumulative share (0..1) at the end of each bin. */
  readonly cumulative: readonly number[];
  /** A marker at zero (the promised time) for a signed distribution. */
  readonly zeroMarker: boolean;
}
export interface FunnelModel {
  readonly kind: 'funnel';
  readonly steps: readonly { readonly key: string; readonly metric: string; readonly value: number | null }[];
}
export interface CohortModel {
  readonly kind: 'cohort';
  readonly cohorts: readonly { readonly cohort: string; readonly size: number; readonly retained: readonly (number | null)[] }[];
  /** Cohorts left out because the period does not hold their first month. */
  readonly skipped: number;
}
export interface WaterfallModel {
  readonly kind: 'waterfall';
  readonly groups: readonly { readonly currency: string | undefined; readonly steps: readonly { readonly key: string; readonly metric: string; readonly value: number | null; readonly total: boolean }[] }[];
}
export interface InsightSeriesGroup {
  readonly currency: string | undefined;
  readonly categories: readonly string[];
  readonly series: readonly { readonly key: string; readonly metric: string | null; readonly values: readonly (number | null)[] }[];
}
export interface InsightSeriesModel {
  readonly kind: 'columns' | 'combo' | 'stackedBars' | 'stackedArea';
  readonly dimension: string;
  readonly unit: string;
  readonly groups: readonly InsightSeriesGroup[];
  /** combo: the keys of the series drawn as lines on the right axis. */
  readonly lines?: readonly string[];
  readonly lineUnit?: string;
  readonly percent?: boolean;
  /** A series per value of this dimension (the payment method): its keys are values, not metrics. */
  readonly seriesDimension?: string;
}
export interface GeoModel {
  readonly kind: 'geo';
  readonly metric: string;
  readonly cells: readonly { readonly key: string; readonly south: number; readonly north: number; readonly west: number; readonly east: number; readonly value: number }[];
  readonly dropped: number;
}
export interface TableColumnModel {
  readonly key: string;
  readonly metric: string | null;
  readonly unit: string;
}
export interface TableModel {
  readonly kind: 'table';
  readonly dimension: string;
  readonly columns: readonly TableColumnModel[];
  readonly groups: readonly { readonly currency: string | undefined; readonly rows: readonly { readonly key: string; readonly label: string | null; readonly marketId: string | undefined; readonly values: Readonly<Record<string, number | null>> }[] }[];
}
export type InsightModel = ScorecardModel | HeatmapModel | HistogramModel | FunnelModel | CohortModel | WaterfallModel | InsightSeriesModel | GeoModel | TableModel;

export interface BiInsightDef {
  readonly id: string;
  readonly kind: InsightKind;
  readonly titleId: string;
  /** A sentence under the title (the rule of the figure: the floor, the grace...). */
  readonly noteId?: string;
  readonly blocks: readonly InsightBlock[];
  /** Spans the whole row. */
  readonly wide?: boolean;
  /** The Explorer question this insight opens ("see the rows"): the id of the block to hand over. */
  readonly drill: string;
  readonly build: (views: InsightViews, env: InsightEnv) => InsightModel;
}

// ───────────────────────────── helpers ─────────────────────────────

const unitOf = (view: AnalyticsBiQueryView | undefined, metric: string): string => view?.metrics.find((m) => m.id === metric)?.unit ?? 'count';
const cellOf = (row: ResultRow, metric: string): ResultCell | undefined => row.values[metric];
const isAdditiveUnit = (unit: string): boolean => unit !== 'ratio' && unit !== 'decimal' && unit !== 'delta_s' && unit !== 'duration_s';

/** The rows of an answer split by currency (a money answer is never summed across them). A non-money answer is one group. */
function byCurrency(view: AnalyticsBiQueryView | undefined): [string | undefined, ResultRow[]][] {
  if (view === undefined) return [];
  const groups = new Map<string, ResultRow[]>();
  for (const row of view.rows) {
    const k = row.currency ?? '';
    const list = groups.get(k) ?? [];
    list.push(row);
    groups.set(k, list);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([k, rows]) => [k === '' ? undefined : k, rows]);
}

/** The single market an answer covers, or null. */
export function singleMarket(views: InsightViews): string | null {
  const ids = new Set<string>();
  for (const v of Object.values(views)) for (const m of v?.scope.markets ?? []) ids.add(m);
  return ids.size === 1 ? ([...ids][0] as string) : null;
}

export function targetFor(targets: InsightEnv['targets'], metric: string, marketId: string | null): BiTarget | null {
  if (marketId === null) return null;
  const hit = targets.find((t) => t.metric === metric && t.marketId === marketId);
  return hit === undefined ? null : { metric: hit.metric, marketId: hit.marketId, value: hit.value, bandLow: hit.bandLow, bandHigh: hit.bandHigh, lowerIsBetter: hit.lowerIsBetter };
}

// ───────────────────────────── builders ─────────────────────────────

/** The figures of the metrics (from any of the answers) against the goals of the one market in view. */
export function scorecard(metrics: readonly string[], views: InsightViews, env: InsightEnv): ScorecardModel {
  const marketId = singleMarket(views);
  const items = metrics.map((metric): ScoreItem => {
    let cell: ResultCell | undefined;
    let base: ResultCell | undefined;
    let unit = 'count';
    for (const view of Object.values(views)) {
      if (view === undefined || !view.metrics.some((m) => m.id === metric)) continue;
      unit = unitOf(view, metric);
      const row = (view.totals?.[0] ?? view.rows[0]) as { values: Readonly<Record<string, ResultCell>>; baseline?: Readonly<Record<string, ResultCell>> } | undefined;
      cell = row?.values[metric];
      base = row?.baseline?.[metric];
      break;
    }
    const value = cell === undefined || cell.value === null ? null : env.num(unit, cell, undefined);
    const baseline = base === undefined || base.value === null ? null : env.num(unit, base, undefined);
    const target = targetFor(env.targets, metric, marketId);
    return { metric, unit, value, baseline, target, status: value !== null && target !== null ? targetStatus(value, target) : null };
  });
  return { kind: 'scorecard', items, marketId };
}

/** A weekday × shift grid of one measure. */
export function heatmap(view: AnalyticsBiQueryView | undefined, metric: string, rowDimension: string, colDimension: string, env: InsightEnv): HeatmapModel {
  const unit = unitOf(view, metric);
  const rowKeys = rowDimension === 'weekday' ? ['1', '2', '3', '4', '5', '6', '7'] : [];
  const colKeys = colDimension === 'shift' ? bucketRanges(BUCKET_EDGES.shift).map((b) => b.key) : [];
  const grid = rowKeys.map(() => colKeys.map((): number | null => (isAdditiveUnit(unit) ? 0 : null)));
  for (const row of view?.rows ?? []) {
    const r = rowKeys.indexOf(row.keys[rowDimension]?.key ?? '');
    const c = colKeys.indexOf(row.keys[colDimension]?.key ?? '');
    const cell = cellOf(row, metric);
    if (r < 0 || c < 0 || cell === undefined || cell.value === null) continue;
    (grid[r] as (number | null)[])[c] = env.num(unit, cell, row.currency);
  }
  return { kind: 'heatmap', metric, unit, rowDimension, colDimension, rowKeys, colKeys, values: grid };
}

/** The distribution of a measure over the buckets of a duration, with its approximate percentiles. */
export function histogram(view: AnalyticsBiQueryView | undefined, dimension: 'match_time_bucket' | 'pickup_time_bucket' | 'eta_deviation_bucket', metric: string, opts: { readonly unit: 'duration_s' | 'delta_s'; readonly lowest: number; readonly highest: number; readonly zeroMarker?: boolean }): HistogramModel {
  const edges = BUCKET_EDGES[dimension];
  const counts: Record<string, number> = {};
  let missing = 0;
  for (const row of view?.rows ?? []) {
    const n = Number(cellOf(row, metric)?.value ?? 0);
    const key = row.keys[dimension]?.key ?? null;
    if (key === null) missing += n;
    else counts[key] = (counts[key] ?? 0) + n;
  }
  const ranges = bucketRanges(edges);
  const bins: HistogramBin[] = ranges.map((r) => ({ key: r.key, from: r.from ?? opts.lowest, to: r.to ?? opts.highest, count: counts[r.key] ?? 0 }));
  const total = bins.reduce((a, b) => a + b.count, 0);
  const input = { counts, edges, lowest: opts.lowest, highest: opts.highest };
  return {
    kind: 'histogram',
    dimension,
    metric,
    unit: opts.unit,
    bins,
    missing,
    total,
    p50: percentileFromBuckets(input, 0.5),
    p90: percentileFromBuckets(input, 0.9),
    cumulative: cumulativeShares(counts, edges),
    zeroMarker: opts.zeroMarker === true,
  };
}

/** A funnel: each step is the first figure of a metric from one of the answers. */
export function funnel(steps: readonly { readonly key: string; readonly metric: string; readonly block: string }[], views: InsightViews, env: InsightEnv): FunnelModel {
  return {
    kind: 'funnel',
    steps: steps.map((s) => {
      const view = views[s.block];
      const row = view?.totals?.[0] ?? view?.rows[0];
      const cell = row?.values[s.metric];
      return { key: s.key, metric: s.metric, value: cell === undefined || cell.value === null ? null : env.num(unitOf(view, s.metric), cell, undefined) };
    }),
  };
}

/** The retention triangle: customers active per cohort month and age in months. A cohort whose first month the period does not hold is left out. */
export function cohort(view: AnalyticsBiQueryView | undefined, metric: string): CohortModel {
  const rows = new Map<string, Map<number, number>>();
  let lastMonth = '';
  for (const row of view?.rows ?? []) {
    const month = row.keys.cohort_month?.key ?? null;
    const age = row.keys.cohort_age?.key ?? null;
    const n = Number(cellOf(row, metric)?.value ?? 0);
    if (month === null || age === null) continue;
    const ages = rows.get(month) ?? new Map<number, number>();
    ages.set(Number(age), (ages.get(Number(age)) ?? 0) + n);
    rows.set(month, ages);
    const active = addMonths(month, Number(age));
    if (active > lastMonth) lastMonth = active;
  }
  const out: CohortModel['cohorts'][number][] = [];
  let skipped = 0;
  for (const [month, ages] of [...rows.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    const size = ages.get(0);
    if (size === undefined || size === 0) {
      skipped += 1;
      continue;
    }
    const observable = monthsBetween(month, lastMonth);
    const maxAge = Math.max(...ages.keys());
    out.push({ cohort: month, size, retained: Array.from({ length: Math.max(observable, maxAge) + 1 }, (_, age) => (age > observable ? null : (ages.get(age) ?? 0))) });
  }
  return { kind: 'cohort', cohorts: out, skipped };
}

function addMonths(month: string, n: number): string {
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7)) - 1 + n;
  return `${y + Math.floor(m / 12)}-${String((m % 12) + 1).padStart(2, '0')}`;
}
function monthsBetween(a: string, b: string): number {
  return (Number(b.slice(0, 4)) - Number(a.slice(0, 4))) * 12 + Number(b.slice(5, 7)) - Number(a.slice(5, 7));
}

/** A waterfall per currency over the figures of one answer: `total` steps are bars from zero (the start and the subtotals), the others change the running figure by `sign`. */
export function waterfall(view: AnalyticsBiQueryView | undefined, steps: readonly { readonly metric: string; readonly total?: boolean; readonly sign?: 1 | -1 }[], env: InsightEnv): WaterfallModel {
  return {
    kind: 'waterfall',
    groups: byCurrency(view).map(([currency, rows]) => {
      const row = rows[0] as ResultRow;
      return {
        currency,
        steps: steps.map((s) => {
          const cell = row.values[s.metric];
          const v = cell === undefined || cell.value === null ? null : env.num(unitOf(view, s.metric), cell, currency);
          return { key: s.metric, metric: s.metric, value: v === null ? null : v * (s.sign ?? 1), total: s.total === true };
        }),
      };
    }),
  };
}

/** Series by one dimension: a series per metric (`bySeries` undefined) or a series per value of a second dimension (`bySeries`). */
export function seriesModel(
  kind: InsightSeriesModel['kind'],
  view: AnalyticsBiQueryView | undefined,
  spec: { readonly dimension: string; readonly metrics: readonly string[]; readonly bySeries?: string; readonly keys?: readonly string[]; readonly percent?: boolean; readonly lines?: readonly string[]; readonly extra?: AnalyticsBiQueryView | undefined },
  env: InsightEnv,
): InsightSeriesModel {
  const unit = unitOf(view, spec.metrics[0] ?? '');
  const dimension = spec.dimension === 'grain' ? env.grain : spec.dimension;
  const groups: InsightSeriesGroup[] = byCurrency(view).map(([currency, rows]) => {
    const cats = new Set<string>(spec.keys ?? []);
    const seriesKeys: { key: string; metric: string | null }[] = [];
    const cellsAt = new Map<string, number>();
    for (const row of rows) {
      const cat = row.keys[dimension]?.key ?? '';
      cats.add(cat);
      if (spec.bySeries !== undefined) {
        const sk = row.keys[spec.bySeries]?.key ?? '';
        if (!seriesKeys.some((s) => s.key === sk)) seriesKeys.push({ key: sk, metric: null });
        const cell = cellOf(row, spec.metrics[0] as string);
        if (cell !== undefined && cell.value !== null) cellsAt.set(`${sk}|${cat}`, env.num(unit, cell, currency));
      } else {
        for (const metric of spec.metrics) {
          if (!seriesKeys.some((s) => s.key === metric)) seriesKeys.push({ key: metric, metric });
          const cell = cellOf(row, metric);
          if (cell !== undefined && cell.value !== null) cellsAt.set(`${metric}|${cat}`, env.num(unitOf(view, metric), cell, currency));
        }
      }
    }
    // a second answer (the demand beside the supply) joins on the same category key
    if (spec.extra !== undefined) {
      for (const row of spec.extra.rows) {
        const cat = row.keys[dimension]?.key ?? '';
        cats.add(cat);
        for (const metric of spec.lines ?? []) {
          const cell = cellOf(row, metric);
          if (cell === undefined || cell.value === null) continue;
          if (!seriesKeys.some((s) => s.key === metric)) seriesKeys.push({ key: metric, metric });
          cellsAt.set(`${metric}|${cat}`, env.num(unitOf(spec.extra, metric), cell, undefined));
        }
      }
    }
    // a time axis is the whole window (a day with no row is a real zero), the others are the keys the answer carried
    const categories = dimension === env.grain && view !== undefined ? trendKeys(env.grain, view.period, [...cats]) : [...cats].sort();
    const additive = isAdditiveUnit(unit);
    return {
      currency,
      categories,
      series: seriesKeys.map((s) => ({
        key: s.key,
        metric: s.metric,
        values: categories.map((c) => cellsAt.get(`${s.key}|${c}`) ?? (additive ? 0 : null)),
      })),
    };
  });
  return { kind, dimension, unit, groups, ...(spec.lines !== undefined ? { lines: spec.lines } : {}), ...(spec.percent === true ? { percent: true } : {}), ...(spec.bySeries !== undefined ? { seriesDimension: spec.bySeries } : {}) };
}

/** The cells of the pickup map: every key that rebuilds a cell; a malformed key is dropped (and counted), never drawn. */
export function geo(view: AnalyticsBiQueryView | undefined, metric: string): GeoModel {
  const cells: GeoModel['cells'][number][] = [];
  let dropped = 0;
  for (const row of view?.rows ?? []) {
    const key = row.keys.pickup_cell?.key ?? null;
    const b = key === null ? null : pickupCellBounds(key);
    const n = Number(cellOf(row, metric)?.value ?? 0);
    if (b === null || key === null) {
      dropped += 1;
      continue;
    }
    cells.push({ key, south: b.south, north: b.north, west: b.west, east: b.east, value: n });
  }
  return { kind: 'geo', metric, cells, dropped };
}

/** A table by one dimension, one column per (metric, answer); columns can come from two answers joined on the row key. */
export function tableModel(spec: { readonly dimension: string; readonly columns: readonly { readonly key: string; readonly metric: string | null; readonly block: string }[] }, views: InsightViews, env: InsightEnv): TableModel {
  const first = views[spec.columns[0]?.block ?? ''];
  const columns: TableColumnModel[] = spec.columns.map((c) => ({ key: c.key, metric: c.metric, unit: c.metric === null ? 'count' : unitOf(views[c.block], c.metric) }));
  const rowsOfBlock = (block: string): readonly ResultRow[] => views[block]?.rows ?? [];
  const groups = byCurrency(first).map(([currency, rows]) => ({
    currency,
    rows: rows.map((row) => {
      const key = row.keys[spec.dimension]?.key ?? '';
      const values: Record<string, number | null> = {};
      for (const c of spec.columns) {
        if (c.metric === null) continue;
        const source = c.block === spec.columns[0]?.block ? row : rowsOfBlock(c.block).find((r) => (r.keys[spec.dimension]?.key ?? '') === key && (r.marketId ?? '') === (row.marketId ?? ''));
        const cell = source?.values[c.metric];
        values[c.key] = cell === undefined || cell.value === null ? null : env.num(unitOf(views[c.block], c.metric), cell, currency);
      }
      return { key, label: row.keys[spec.dimension]?.label ?? null, marketId: row.marketId, values };
    }),
  }));
  return { kind: 'table', dimension: spec.dimension, columns, groups };
}

// ───────────────────────────── the definitions ─────────────────────────────

const desc = (by: string): { by: string; direction: 'desc' } => ({ by, direction: 'desc' });
/** Rides left out (the bridges below are the identities of a food or delivery booking: gross = products + delivery - discount, partner net = products - commission). */
const notRides = { dimension: 'vertical', op: 'not_in' as const, values: ['RIDE'] };
const block = (id: string, b: Omit<BiBlock, 'id'>, extra: Omit<InsightBlock, 'id' | 'block'> = {}): InsightBlock => ({ id, block: { id, ...b }, ...extra });

/** The Operação insights (stage times, ETA, demand heat, the match curve). */
export const OPERATION_INSIGHTS: readonly BiInsightDef[] = [
  {
    id: 'scorecard',
    kind: 'scorecard',
    titleId: 'bi.ins.scorecard',
    noteId: 'bi.ins.scorecard.note',
    wide: true,
    drill: 'score',
    blocks: [block('score', { metrics: ['eta_on_time_rate', 'accept_time', 'pickup_time', 'cancellation_rate'] }, { comparison: true })],
    build: (views, env) => scorecard(['eta_on_time_rate', 'accept_time', 'pickup_time', 'cancellation_rate'], views, env),
  },
  {
    id: 'stages',
    kind: 'stackedBars',
    titleId: 'bi.ins.stages',
    noteId: 'bi.ins.stages.note',
    drill: 'stages',
    blocks: [block('stages', { metrics: ['accept_time', 'pickup_time', 'trip_time', 'prep_time'], dimensions: ['vertical'], limit: 10 })],
    build: (views, env) => seriesModel('stackedBars', views.stages, { dimension: 'vertical', metrics: ['accept_time', 'pickup_time', 'trip_time', 'prep_time'] }, env),
  },
  {
    id: 'heatDemand',
    kind: 'heatmap',
    titleId: 'bi.ins.heatDemand',
    noteId: 'bi.ins.heat.note',
    wide: true,
    drill: 'heat',
    blocks: [block('heat', { metrics: ['orders'], dimensions: ['weekday', 'shift'], limit: 200 })],
    build: (views, env) => heatmap(views.heat, 'orders', 'weekday', 'shift', env),
  },
  {
    id: 'heatCancel',
    kind: 'heatmap',
    titleId: 'bi.ins.heatCancel',
    noteId: 'bi.ins.heat.note',
    wide: true,
    drill: 'heat',
    blocks: [block('heat', { metrics: ['orders_cancelled'], dimensions: ['weekday', 'shift'], limit: 200 })],
    build: (views, env) => heatmap(views.heat, 'orders_cancelled', 'weekday', 'shift', env),
  },
  {
    id: 'matchCurve',
    kind: 'histogram',
    titleId: 'bi.ins.matchCurve',
    noteId: 'bi.ins.percentile.note',
    drill: 'match',
    blocks: [block('match', { metrics: ['rides_requested'], dimensions: ['match_time_bucket'], limit: 50 })],
    build: (views) => histogram(views.match, 'match_time_bucket', 'rides_requested', { unit: 'duration_s', lowest: 0, highest: 420 }),
  },
  {
    id: 'pickupCurve',
    kind: 'histogram',
    titleId: 'bi.ins.pickupCurve',
    noteId: 'bi.ins.percentile.note',
    drill: 'pickup',
    blocks: [block('pickup', { metrics: ['orders'], dimensions: ['pickup_time_bucket'], limit: 50 })],
    build: (views) => histogram(views.pickup, 'pickup_time_bucket', 'orders', { unit: 'duration_s', lowest: 0, highest: 1800 }),
  },
  {
    id: 'etaDeviation',
    kind: 'histogram',
    titleId: 'bi.ins.etaDeviation',
    noteId: 'bi.ins.etaDeviation.note',
    drill: 'eta',
    blocks: [block('eta', { metrics: ['orders'], dimensions: ['eta_deviation_bucket'], limit: 50 })],
    build: (views) => histogram(views.eta, 'eta_deviation_bucket', 'orders', { unit: 'delta_s', lowest: -600, highest: 900, zeroMarker: true }),
  },
];

/** The Crescimento insights (new against returning, cohorts, the ride funnel). */
export const GROWTH_INSIGHTS: readonly BiInsightDef[] = [
  {
    id: 'mix',
    kind: 'stackedArea',
    titleId: 'bi.ins.mix',
    noteId: 'bi.ins.mix.note',
    wide: true,
    drill: 'mix',
    blocks: [block('mix', { metrics: ['new_customers', 'returning_customers'] }, { grain: true })],
    build: (views, env) => seriesModel('stackedArea', views.mix, { dimension: 'grain', metrics: ['new_customers', 'returning_customers'] }, env),
  },
  {
    id: 'cohort',
    kind: 'cohort',
    titleId: 'bi.ins.cohort',
    noteId: 'bi.ins.cohort.note',
    wide: true,
    drill: 'cohort',
    blocks: [block('cohort', { metrics: ['active_customers'], dimensions: ['cohort_month', 'cohort_age'], limit: 1000 })],
    build: (views) => cohort(views.cohort, 'active_customers'),
  },
  {
    id: 'funnel',
    kind: 'funnel',
    titleId: 'bi.ins.funnel',
    noteId: 'bi.ins.funnel.note',
    drill: 'steps',
    blocks: [block('quotes', { metrics: ['quotes'] }), block('steps', { metrics: ['funnel_requested', 'funnel_matched', 'funnel_completed'] })],
    build: (views, env) =>
      funnel(
        [
          { key: 'quote', metric: 'quotes', block: 'quotes' },
          { key: 'request', metric: 'funnel_requested', block: 'steps' },
          { key: 'match', metric: 'funnel_matched', block: 'steps' },
          { key: 'complete', metric: 'funnel_completed', block: 'steps' },
        ],
        views,
        env,
      ),
  },
];

/** The additions to Financeiro (bridges, value lost by shift, the payment mix, refunds, coupons). */
export const FINANCE_INSIGHTS: readonly BiInsightDef[] = [
  {
    id: 'bridgeSales',
    kind: 'waterfall',
    titleId: 'bi.ins.bridgeSales',
    noteId: 'bi.ins.bridge.note',
    drill: 'bridge',
    blocks: [block('bridge', { metrics: ['merchandise', 'delivery_fees', 'discounts', 'gmv'], filters: [notRides] })],
    build: (views, env) =>
      waterfall(views.bridge, [{ metric: 'merchandise', total: true }, { metric: 'delivery_fees' }, { metric: 'discounts', sign: -1 }, { metric: 'gmv', total: true }], env),
  },
  {
    id: 'bridgePartner',
    kind: 'waterfall',
    titleId: 'bi.ins.bridgePartner',
    noteId: 'bi.ins.bridge.note',
    drill: 'bridge',
    blocks: [block('bridge', { metrics: ['merchandise', 'commission', 'partner_net'], filters: [notRides] })],
    build: (views, env) => waterfall(views.bridge, [{ metric: 'merchandise', total: true }, { metric: 'commission', sign: -1 }, { metric: 'partner_net', total: true }], env),
  },
  {
    id: 'lostByShift',
    kind: 'columns',
    titleId: 'bi.ins.lostByShift',
    noteId: 'bi.ins.lost.note',
    wide: true,
    drill: 'lost',
    blocks: [block('lost', { metrics: ['cancelled_value', 'no_driver_value'], dimensions: ['shift'], limit: 100 })],
    build: (views, env) => seriesModel('columns', views.lost, { dimension: 'shift', metrics: ['cancelled_value', 'no_driver_value'], keys: bucketRanges(BUCKET_EDGES.shift).map((b) => b.key) }, env),
  },
  {
    id: 'paymentMix',
    kind: 'stackedArea',
    titleId: 'bi.ins.paymentMix',
    wide: true,
    drill: 'mix',
    blocks: [block('mix', { metrics: ['orders'], dimensions: ['payment_method'], limit: 400 }, { grain: true })],
    build: (views, env) => seriesModel('stackedArea', views.mix, { dimension: 'grain', metrics: ['orders'], bySeries: 'payment_method', percent: true }, env),
  },
  {
    id: 'refunds',
    kind: 'columns',
    titleId: 'bi.ins.refunds',
    noteId: 'bi.ins.refunds.note',
    drill: 'refunds',
    blocks: [block('refunds', { metrics: ['refund_amount'], dimensions: ['issue_reason'], sort: [desc('refund_amount')], limit: 12 })],
    build: (views, env) => seriesModel('columns', views.refunds, { dimension: 'issue_reason', metrics: ['refund_amount'] }, env),
  },
  {
    id: 'promos',
    kind: 'table',
    titleId: 'bi.ins.promos',
    noteId: 'bi.ins.promos.note',
    drill: 'promos',
    blocks: [block('promos', { metrics: ['promo_redemptions', 'promo_discount', 'promo_discount_rate'], dimensions: ['coupon'], sort: [desc('promo_discount')], limit: 12 })],
    build: (views, env) =>
      tableModel(
        {
          dimension: 'coupon',
          columns: [
            { key: 'coupon', metric: null, block: 'promos' },
            { key: 'promo_redemptions', metric: 'promo_redemptions', block: 'promos' },
            { key: 'promo_discount', metric: 'promo_discount', block: 'promos' },
            { key: 'promo_discount_rate', metric: 'promo_discount_rate', block: 'promos' },
          ],
        },
        views,
        env,
      ),
  },
];

/** The Oferta insights (supply hours and utilisation, demand against supply, offers). */
export const SUPPLY_INSIGHTS: readonly BiInsightDef[] = [
  {
    id: 'scorecard',
    kind: 'scorecard',
    titleId: 'bi.ins.scorecard',
    noteId: 'bi.ins.scorecard.note',
    wide: true,
    drill: 'score',
    blocks: [block('score', { metrics: ['supply_utilisation'] }, { comparison: true }), block('offers', { metrics: ['offer_acceptance_rate', 'offer_lapse_rate'] }, { comparison: true })],
    build: (views, env) => scorecard(['supply_utilisation', 'offer_acceptance_rate', 'offer_lapse_rate'], views, env),
  },
  {
    id: 'utilisation',
    kind: 'combo',
    titleId: 'bi.ins.utilisation',
    noteId: 'bi.ins.supply.note',
    wide: true,
    drill: 'util',
    blocks: [block('util', { metrics: ['supply_online_time', 'supply_utilisation'], dimensions: ['shift'], limit: 50 })],
    build: (views, env) => ({ ...seriesModel('combo', views.util, { dimension: 'shift', metrics: ['supply_online_time', 'supply_utilisation'], lines: ['supply_utilisation'], keys: bucketRanges(BUCKET_EDGES.shift).map((b) => b.key) }, env), lineUnit: 'ratio' }),
  },
  {
    id: 'demandSupply',
    kind: 'combo',
    titleId: 'bi.ins.demandSupply',
    noteId: 'bi.ins.supply.note',
    wide: true,
    drill: 'demand',
    blocks: [block('demand', { metrics: ['orders'], dimensions: ['shift'], limit: 50 }), block('supply', { metrics: ['supply_avg_drivers'], dimensions: ['shift'], limit: 50 })],
    build: (views, env) => ({ ...seriesModel('combo', views.demand, { dimension: 'shift', metrics: ['orders'], lines: ['supply_avg_drivers'], extra: views.supply, keys: bucketRanges(BUCKET_EDGES.shift).map((b) => b.key) }, env), lineUnit: 'decimal' }),
  },
  {
    id: 'heatSupply',
    kind: 'heatmap',
    titleId: 'bi.ins.heatSupply',
    noteId: 'bi.ins.heat.note',
    wide: true,
    drill: 'heat',
    blocks: [block('heat', { metrics: ['supply_online_time'], dimensions: ['weekday', 'shift'], limit: 200 })],
    build: (views, env) => heatmap(views.heat, 'supply_online_time', 'weekday', 'shift', env),
  },
  {
    id: 'offerWaves',
    kind: 'stackedBars',
    titleId: 'bi.ins.offerWaves',
    drill: 'waves',
    blocks: [block('waves', { metrics: ['offers_accepted', 'offers_declined', 'offers_lapsed'], dimensions: ['offer_wave'], limit: 20 })],
    build: (views, env) => seriesModel('stackedBars', views.waves, { dimension: 'offer_wave', metrics: ['offers_accepted', 'offers_declined', 'offers_lapsed'] }, env),
  },
];

/** The Mapa insight (demand by pickup cell). */
export const MAP_INSIGHTS: readonly BiInsightDef[] = [
  {
    id: 'demandMap',
    kind: 'geo',
    titleId: 'bi.ins.demandMap',
    noteId: 'bi.ins.map.note',
    wide: true,
    drill: 'cells',
    blocks: [block('cells', { metrics: ['cell_orders'], dimensions: ['pickup_cell'], sort: [desc('cell_orders')], limit: 500 })],
    build: (views) => geo(views.cells, 'cell_orders'),
  },
];

/** The Qualidade insights (scorecard, store availability, issues against orders). */
export const QUALITY_INSIGHTS: readonly BiInsightDef[] = [
  {
    id: 'scorecard',
    kind: 'scorecard',
    titleId: 'bi.ins.scorecard',
    noteId: 'bi.ins.scorecard.note',
    wide: true,
    drill: 'ratings',
    blocks: [block('ratings', { metrics: ['rating_average', 'low_rating_share'] }, { comparison: true }), block('stores', { metrics: ['store_availability_rate'] }, { comparison: true })],
    build: (views, env) => scorecard(['rating_average', 'low_rating_share', 'store_availability_rate'], views, env),
  },
  {
    id: 'ratingDist',
    kind: 'columns',
    titleId: 'bi.ins.ratingDist',
    drill: 'dist',
    blocks: [block('dist', { metrics: ['ratings'], dimensions: ['stars'], limit: 10 }, { ignoreBar: 'stars' })],
    build: (views, env) => seriesModel('columns', views.dist, { dimension: 'stars', metrics: ['ratings'], keys: ['1', '2', '3', '4', '5'] }, env),
  },
  {
    id: 'stores',
    kind: 'table',
    titleId: 'bi.ins.stores',
    noteId: 'bi.ins.stores.note',
    wide: true,
    drill: 'stores',
    blocks: [block('stores', { metrics: ['store_availability_rate', 'store_live_seconds', 'store_pauses'], dimensions: ['location'], sort: [{ by: 'store_availability_rate', direction: 'asc' }], limit: 15 })],
    build: (views, env) =>
      tableModel(
        {
          dimension: 'location',
          columns: [
            { key: 'location', metric: null, block: 'stores' },
            { key: 'store_availability_rate', metric: 'store_availability_rate', block: 'stores' },
            { key: 'store_live_seconds', metric: 'store_live_seconds', block: 'stores' },
            { key: 'store_pauses', metric: 'store_pauses', block: 'stores' },
          ],
        },
        views,
        env,
      ),
  },
  {
    id: 'issueRate',
    kind: 'table',
    titleId: 'bi.ins.issueRate',
    noteId: 'bi.ins.issueRate.note',
    wide: true,
    drill: 'issues',
    blocks: [block('issues', { metrics: ['issues', 'refunds'], dimensions: ['market'], limit: 20 }), block('orders', { metrics: ['orders_completed'], dimensions: ['market'], limit: 20 })],
    build: (views, env) => {
      const model = tableModel(
        {
          dimension: 'market',
          columns: [
            { key: 'market', metric: null, block: 'issues' },
            { key: 'orders_completed', metric: 'orders_completed', block: 'orders' },
            { key: 'issues', metric: 'issues', block: 'issues' },
            { key: 'refunds', metric: 'refunds', block: 'issues' },
          ],
        },
        views,
        env,
      );
      // the rate is the one derived column: issues ÷ completed orders of the same market (two answers, so it is computed here and labelled as such)
      return {
        ...model,
        columns: [...model.columns, { key: 'issue_rate', metric: null, unit: 'ratio' }],
        groups: model.groups.map((g) => ({
          ...g,
          rows: g.rows.map((r) => {
            const orders = r.values.orders_completed;
            const issues = r.values.issues;
            return { ...r, values: { ...r.values, issue_rate: orders === null || orders === undefined || orders === 0 || issues === null || issues === undefined ? null : issues / orders } };
          }),
        })),
      };
    },
  },
];
